import {
  Badge,
  Box,
  Flex,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useTranslation } from 'react-i18next';
import useBusinessDetail from './hooks/useBusinessDetail';
import SubscriptionPanel from './components/SubscriptionPanel';
import LinkedUsersTable from './components/LinkedUsersTable';
import BusinessContactsTable from './components/BusinessContactsTable';
import BusinessEventsTable from './components/BusinessEventsTable';

export default function BusinessDetail() {
  const { t } = useTranslation();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const {
    business,
    linkedUsers,
    allPlans,
    loading,
    saving,
    updateSubscription,
    switchPlan,
  } = useBusinessDetail();

  if (loading) {
    return (
      <Flex
        direction="column"
        pt={{ sm: '125px', lg: '75px' }}
        align="center"
        justify="center"
        minH="50vh"
      >
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (!business) {
    return (
      <Flex direction="column" pt={{ sm: '125px', lg: '75px' }} align="center">
        <Text color="red.500" fontSize="lg">
          Business not found
        </Text>
      </Flex>
    );
  }

  const createdByUser =
    typeof business.createdBy === 'object' ? business.createdBy : null;

  const formatDate = (date?: string | Date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  return (
    <Flex direction="column" pt={{ sm: '125px', lg: '75px' }} gap="20px">
      {/* Business Header */}
      <Flex justify="space-between" align="center">
        <Box>
          <Text color={textColor} fontSize="2xl" fontWeight="700">
            {business.businessName}
          </Text>
          <Text color="gray.500" fontSize="sm">
            ID: {business.id}
          </Text>
        </Box>
        {business.subscription?.type && (
          <Badge
            colorScheme={
              business.subscription.type === 'active'
                ? 'green'
                : business.subscription.type === 'trial'
                  ? 'blue'
                  : business.subscription.type === 'cancelled'
                    ? 'red'
                    : 'gray'
            }
            fontSize="md"
            px="12px"
            py="4px"
            borderRadius="8px"
          >
            {business.subscription.type}
          </Badge>
        )}
      </Flex>

      {/* Tabs */}
      <Tabs colorScheme="brand" isLazy>
        <TabList>
          <Tab>Overview</Tab>
          <Tab>Users</Tab>
          <Tab>Contacts</Tab>
          <Tab>Events</Tab>
        </TabList>

        <TabPanels>
          {/* Tab 1: Overview */}
          <TabPanel px="0">
            <Flex direction="column" gap="20px">
              {/* Business Info Card */}
              <Card p="30px">
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="20px">
                  <Box>
                    <Text fontSize="sm" color="gray.500">
                      {t('business_table.email') || 'Email'}
                    </Text>
                    <Text fontSize="md" color={textColor}>
                      {business.email || '-'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">
                      {t('business_table.phone') || 'Phone'}
                    </Text>
                    <Text fontSize="md" color={textColor}>
                      {business.phone || '-'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">
                      VAT ID
                    </Text>
                    <Text fontSize="md" color={textColor}>
                      {business.businessVATId || '-'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">
                      Address
                    </Text>
                    <Text fontSize="md" color={textColor}>
                      {business.address || '-'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">
                      Created At
                    </Text>
                    <Text fontSize="md" color={textColor}>
                      {formatDate(business.createdAt)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.500">
                      Created By
                    </Text>
                    <Text fontSize="md" color={textColor}>
                      {createdByUser
                        ? `${createdByUser.firstName || createdByUser.name || ''} ${createdByUser.familyName || ''} (${createdByUser.email})`
                        : typeof business.createdBy === 'string'
                          ? business.createdBy
                          : '-'}
                    </Text>
                  </Box>
                </SimpleGrid>
              </Card>

              {/* Subscription Panel */}
              <SubscriptionPanel
                business={business}
                allPlans={allPlans}
                saving={saving}
                onUpdateSubscription={updateSubscription}
                onSwitchPlan={switchPlan}
              />
            </Flex>
          </TabPanel>

          {/* Tab 2: Users */}
          <TabPanel px="0">
            <LinkedUsersTable users={linkedUsers} />
          </TabPanel>

          {/* Tab 3: Contacts */}
          <TabPanel px="0">
            {business.id && <BusinessContactsTable businessId={business.id} />}
          </TabPanel>

          {/* Tab 4: Events */}
          <TabPanel px="0">
            {business.id && <BusinessEventsTable businessId={business.id} />}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
}
